/*
/ LIST OF FUNCTIONS
/ - gtmSitewide
/ - gtmAddToCart
/ - gaProductDetail
/ - gtmProductImpression
/ - gtmLogin
/ - gtmLogout
*/

import { cleanProductGID, cleanVariantGID } from '../cleanGIDs';
import getManifestFromCookie from '@/lib/contentstack/getManifestFromCookie';

const userCurrentState = {
  title: '',
  product: {
    name: ''
  },
  checkout: '',
  template: ''
};

const loggedInUserData = {
  displayName: '',
  email: '',
  phone: '',
  id: '',
  customer_status: ''
};

function getPageName(pagetitle) {
  if (userCurrentState.template === 'index') {
    return (userCurrentState.title = 'Home');
  } else {
    userCurrentState.title = pagetitle;
  }

  return userCurrentState.title;
}

function convertstringToInteger(str) {
  return parseInt(str, 10);
}

async function getUserData() {
  const custdata = JSON.parse(window?.localStorage?.getItem('userdata'));

  let customer_status = '';
  if (custdata !== null) {
    customer_status = 'Logged In';
    loggedInUserData.customer_status = customer_status;
    loggedInUserData.displayName = custdata.displayName;
    loggedInUserData.email = custdata.email;
    loggedInUserData.phone = custdata.phone;
    loggedInUserData.id = custdata.id;
  } else {
    customer_status = 'Logged Out';
    loggedInUserData.customer_status = customer_status;
    loggedInUserData.displayName = null;
    loggedInUserData.email = null;
    loggedInUserData.phone = null;
    loggedInUserData.id = null;
  }

  return loggedInUserData;
}

export async function gtmSitewide(pageTitle, templateType) {
  window.dataLayer = window.dataLayer || [];

  const index = window.dataLayer.findIndex((obj) => obj.event === 'sitewide');
  if (index !== -1) {
    window.dataLayer.splice(index, 1);
  }

  const userdata = await getUserData();
  const personalizeData = getPersonalizeExperiences();

  const payloadData = {
    event: 'sitewide',
    page: {
      pageTitle: getPageName(pageTitle),
      checkout: null,
      template: templateType,
      type: templateType
    },
    personalize: {
      experiences: personalizeData?.map((exp) => ({
        experienceUid: exp.shortUid,
        variantUid: exp.activeVariantShortUid
      }))
    },
    user: {
      name: userdata.displayName,
      email: userdata.email,
      phone: userdata.phone,
      id: userdata.id,
      language: 'en-us'
    }
  };

  window.dataLayer.push(payloadData);
}

export function gtmProductDetail(shopifyProduct, selectedVariant) {
  if (!shopifyProduct || !selectedVariant) {
    console.warn('Missing required data for gaProductDetail');
    return;
  }

  const payloadData = {
    event: 'productDetail',
    ecommerce: {
      currencyCode: 'USD',
      detail: {
        products: [
          {
            name: shopifyProduct.title,
            sku: selectedVariant.sku,
            price: convertstringToInteger(selectedVariant.compareAtPrice) || 0,
            category: shopifyProduct.productType,
            position: 1,
            dimension4: shopifyProduct.handle, // Product Handle
            dimension5: convertstringToInteger(
              cleanVariantGID(selectedVariant.id)
            ), // Variant ID
            dimension6: 'Leesa Sleep', // Product Vendor
            dimension7: convertstringToInteger(
              cleanProductGID(shopifyProduct.id)
            ) // Product ID
          }
        ]
      }
    }
  };
  if (window !== undefined) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payloadData);
  }
}

export function gtmProductImpression(productdata) {
  const collectionArr = [];

  productdata.map((item, index) => {
    const itemProduct = item.product
      ? item.product
      : item.productConnection.node;
    if (!itemProduct.length) return null;
    const productdetails = item.product
      ? item.product[0].shopifyProduct[0]
      : item.productConnection.edges[0].node.shopifyProduct[0];
    collectionArr.push({
      name: productdetails.title,
      price:
        convertstringToInteger(productdetails.variants[0].price.amount) ||
        convertstringToInteger(
          productdetails.variants[0].compareAtPrice.amount
        ),
      position: index + 1,
      category: productdetails.productType,
      dimension4: productdetails.handle,
      dimension6: 'Leesa Sleep', // Product Vendor
      dimension7: convertstringToInteger(cleanProductGID(productdetails.id))
    });
  });

  const payloadData = {
    event: 'productImpression',
    ecommerce: {
      currencyCode: 'USD',
      impressions: collectionArr
    }
  };

  if (window !== undefined) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payloadData);
  }
}

export function gtmCrosssellProductImpression(productdata) {
  if (!productdata) return;

  const crosssellCollectionArr = [];
  productdata.map((item, index) => {
    const productdetails = item.reference[0].shopifyProduct[0];
    crosssellCollectionArr.push({
      name: productdetails.title,
      list: 'Product Detail Page',
      price:
        convertstringToInteger(productdetails.variants[0].price.amount) ||
        convertstringToInteger(
          productdetails.variants[0].compareAtPrice.amount
        ),
      position: index + 1,
      category: productdetails.productType,
      dimension4: productdetails.handle,
      dimension6: 'Leesa Sleep', // Product Vendor
      dimension7: convertstringToInteger(cleanProductGID(productdetails.id))
    });
  });

  const payloadData = {
    event: 'productImpression',
    ecommerce: {
      currencyCode: 'USD',
      impressions: crosssellCollectionArr
    }
  };

  if (window !== undefined) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payloadData);
  }
}

export function gtmAddToCart({ itemsToAdd, quantity, page }) {
  const cartAddArr = [];

  itemsToAdd.map((item) => {
    if (item.attributes) {
      item = {
        customAttributes: item.attributes,
        product: item.merchandise.product,
        variant: {
          compareAtPrice: item.merchandise.compareAtPrice,
          id: item.merchandise.id,
          price: item.merchandise.price,
          sku: item.merchandise.sku,
          title: item.merchandise.title
        },
        quantity: quantity
      };
    }
    //if gwp, price is zero
    let itemPrice;
    const isPromo = item.customAttributes.find(
      (attribute) =>
        attribute.key.includes('isPromo') && attribute.value == 'true'
    );

    if (isPromo) {
      itemPrice = 0;
    } else {
      itemPrice =
        item.variant.price.amount || item.variant.compareAtPrice.amount;
    }

    cartAddArr.push({
      quantity: item.quantity || 1,
      name: item.product.title,
      sku: item.variant.sku,
      id: convertstringToInteger(cleanVariantGID(item.variant.id)),
      price: convertstringToInteger(itemPrice),
      category: item.product.productType,
      variant: item.variant.title,
      position: 1,
      dimension4: item.product.handle, // Product Handle
      dimension5: item.variant.title, // Product Variant
      dimension6: 'Leesa Sleep', // Product Vendor
      dimension7: convertstringToInteger(cleanProductGID(item.product.id)) // Product Id
    });
  });

  const personalizeData = getPersonalizeExperiences();

  const payloadData = {
    event: 'addToCart',
    ecommerce: {
      currencyCode: 'USD',
      add: {
        actionField: { Product: page },
        products: cartAddArr
      }
    },
    personalize: {
      experiences: personalizeData?.map((exp) => ({
        experienceUid: exp.shortUid,
        variantUid: exp.activeVariantShortUid
      }))
    }
  };

  if (window !== undefined) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payloadData);
  }
}

export function gtmLogin() {
  if (window !== undefined) {
    const userEmail = JSON.parse(window.localStorage.getItem('userdata'))
      ?.emailAddress?.emailAddress;

    const payloadData = {
      event: 'login',
      account: {
        email: userEmail
      }
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payloadData);
  }
}

export function gtmLogout() {
  if (window !== undefined) {
    const userEmail = JSON.parse(window.localStorage.getItem('userdata'))
      ?.emailAddress?.emailAddress;

    const payloadData = {
      event: 'logout',
      account: {
        email: userEmail
      }
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payloadData);
  }
}

export function getPersonalizeExperiences() {
  const manifest = getManifestFromCookie();

  if (!manifest?.experiences?.length) {
    console.warn('getPersonalizeExperiences returned no experiences');
    return [];
  }

  return manifest?.experiences || [];
}

export function gtmZendeskChatOpened() {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'zendeskChatOpened'
  });
}
